(function () {
  if (window.__slideshowPromotionAnalyticsLoaded) return;
  window.__slideshowPromotionAnalyticsLoaded = true;

  const VIEW_EVENT = 'view_promotion';
  const SELECT_EVENT = 'select_promotion';
  const BANNER_SELECTOR = '[data-promotion-banner]';
  const INTERACTIVE_SELECTOR = [
    'a',
    'button',
    'input',
    'select',
    'textarea',
    'label',
    'summary',
    '[role="button"]',
    '[role="link"]',
  ].join(',');
  const CONTENT_CLICK_SELECTOR = 'a[href], button, [role="button"], [role="link"]';
  const viewedPromotionIds = new Set();

  function isDesignMode() {
    return Boolean(window.Shopify && window.Shopify.designMode);
  }

  function cleanObject(object) {
    return Object.fromEntries(
      Object.entries(object).filter(([, value]) => value !== '' && value !== undefined && value !== null)
    );
  }

  function getDestinationPath(value) {
    if (!value) return '';

    try {
      return new URL(value, window.location.origin).pathname;
    } catch (error) {
      return '';
    }
  }

  function getTriggerLabel(trigger) {
    if (!trigger) return '';

    return (
      trigger.dataset.promotionCtaLabel ||
      trigger.getAttribute('aria-label') ||
      trigger.textContent ||
      ''
    )
      .replace(/\s+/g, ' ')
      .trim();
  }

  function getPromotionItem(banner) {
    return cleanObject({
      item_id: banner.dataset.promotionProductId || '',
      item_name: banner.dataset.promotionProductName || banner.dataset.promotionName || '',
      promotion_id: banner.dataset.promotionId || '',
      promotion_name: banner.dataset.promotionName || '',
      creative_name: banner.dataset.promotionCreativeName || banner.dataset.promotionName || '',
      creative_slot: banner.dataset.promotionCreativeSlot || '',
    });
  }

  function getPromotionPayload(eventName, banner, trigger) {
    const slideIndex = Number(banner.dataset.promotionSlideIndex || 0) || undefined;
    const isPromotionSelection = eventName === SELECT_EVENT;
    const destination =
      trigger?.dataset.promotionDestination || trigger?.getAttribute('href') || '';
    const clickElement = isPromotionSelection
      ? trigger?.dataset.promotionClickElement || (trigger ? 'button' : 'banner')
      : '';

    return cleanObject({
      event: eventName,
      promotion_id: banner.dataset.promotionId || '',
      promotion_name: banner.dataset.promotionName || '',
      creative_name: banner.dataset.promotionCreativeName || banner.dataset.promotionName || '',
      creative_slot: banner.dataset.promotionCreativeSlot || '',
      promotion_location: banner.dataset.promotionLocation || '',
      section_id: banner.dataset.promotionSectionId || '',
      section_type: banner.dataset.promotionSectionType || '',
      slide_index: slideIndex,
      page_location: `${window.location.origin}${window.location.pathname}`,
      page_path: banner.dataset.promotionPagePath || window.location.pathname,
      page_type: banner.dataset.promotionPageType || '',
      product_id: banner.dataset.promotionProductId || '',
      product_handle: banner.dataset.promotionProductHandle || '',
      click_element: clickElement,
      cta_position: isPromotionSelection ? trigger?.dataset.promotionCtaPosition || '' : '',
      cta_label: isPromotionSelection && clickElement !== 'banner' ? getTriggerLabel(trigger) : '',
      destination_path: isPromotionSelection ? getDestinationPath(destination) : '',
      ecommerce: {
        items: [getPromotionItem(banner)],
      },
    });
  }

  function pushPromotionPayload(payload) {
    if (isDesignMode()) return;

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push(payload);
    document.dispatchEvent(
      new CustomEvent(`theme:promotion:${payload.event}`, {
        detail: payload,
      })
    );
  }

  function isMostlyInViewport(element) {
    const rect = element.getBoundingClientRect();
    const visibleWidth = Math.max(0, Math.min(rect.right, window.innerWidth) - Math.max(rect.left, 0));
    const visibleHeight = Math.max(0, Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0));
    const visibleArea = visibleWidth * visibleHeight;
    const elementArea = rect.width * rect.height;

    return elementArea > 0 && visibleArea / elementArea >= 0.5;
  }

  function isActivePromotionBanner(banner) {
    return !banner.classList.contains('swiper-slide') || banner.classList.contains('swiper-slide-active');
  }

  function trackPromotionView(banner) {
    const promotionId = banner.dataset.promotionId;

    if (
      !isActivePromotionBanner(banner) ||
      !isMostlyInViewport(banner) ||
      viewedPromotionIds.has(promotionId)
    ) {
      return;
    }

    viewedPromotionIds.add(promotionId);
    pushPromotionPayload(getPromotionPayload(VIEW_EVENT, banner));
  }

  function observePromotionViews() {
    if (!('IntersectionObserver' in window)) {
      document.querySelectorAll(BANNER_SELECTOR).forEach(trackPromotionView);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) trackPromotionView(entry.target);
        });
      },
      { threshold: 0.5 }
    );

    function observeBanners(root) {
      if (root instanceof Element && root.matches(BANNER_SELECTOR)) observer.observe(root);
      root.querySelectorAll?.(BANNER_SELECTOR).forEach((banner) => observer.observe(banner));
    }

    observeBanners(document);

    new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.target.matches(BANNER_SELECTOR)) {
          trackPromotionView(mutation.target);
        }

        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) observeBanners(node);
        });
      });
    }).observe(document.documentElement, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['class'],
    });

    const trackInitialBanners = () => {
      document.querySelectorAll(BANNER_SELECTOR).forEach(trackPromotionView);
    };

    requestAnimationFrame(trackInitialBanners);
    window.addEventListener('load', trackInitialBanners, { once: true });
  }

  function getInferredPromotionClick(target, banner) {
    if (banner.dataset.promotionTrackContentLinks !== 'true') return null;

    const trigger = target.closest(CONTENT_CLICK_SELECTOR);
    if (!trigger || !banner.contains(trigger) || trigger.closest('[data-promotion-click]')) return null;
    if (trigger.closest('.product-carousel-banner')) return null;
    if (trigger.matches('[aria-disabled="true"], [disabled]')) return null;

    const destination = trigger.dataset.promotionDestination || trigger.getAttribute('href') || '';
    if (!destination || destination === '#') return null;

    return trigger;
  }

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;

    const trackedClick = event.target.closest('[data-promotion-click]');
    if (trackedClick) {
      const banner = trackedClick.closest(BANNER_SELECTOR);
      if (banner) {
        pushPromotionPayload(getPromotionPayload(SELECT_EVENT, banner, trackedClick));
      }
      return;
    }

    const banner = event.target.closest(BANNER_SELECTOR);
    if (!banner) return;

    const inferredClick = getInferredPromotionClick(event.target, banner);
    if (inferredClick) {
      pushPromotionPayload(getPromotionPayload(SELECT_EVENT, banner, inferredClick));
      return;
    }

    if (event.target.closest(INTERACTIVE_SELECTOR)) return;

    pushPromotionPayload(getPromotionPayload(SELECT_EVENT, banner));
  });

  observePromotionViews();
})();
